import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addMovement,
  closeCash,
  openCash,
  sessionSummary,
  sageResync,
  sageToggle,
} from "@/lib/api";
import { useApp } from "@/lib/AppContext";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine, Lock, Unlock, RotateCw, Eye, EyeOff, Power } from "lucide-react";

export default function Cash() {
  const navigate = useNavigate();
  const { user, session, refreshSession, refreshSage } = useApp();
  const [opening, setOpening] = useState("100.00");
  const [summary, setSummary] = useState(null);
  const [closing, setClosing] = useState(false);
  const [counted, setCounted] = useState("");
  const [notes, setNotes] = useState("");
  const [blind, setBlind] = useState(true); // arqueo ciego por defecto

  // Movement form
  const [movKind, setMovKind] = useState("entrada");
  const [movAmount, setMovAmount] = useState("");
  const [movReason, setMovReason] = useState("");

  useEffect(() => {
    if (session) sessionSummary(session.id).then(setSummary);
  }, [session]);

  const reload = async () => {
    const s = await refreshSession();
    if (s) setSummary(await sessionSummary(s.id));
  };

  const doOpen = async (e) => {
    e.preventDefault();
    try {
      await openCash({ opening_amount: parseFloat(opening || "0") }, user);
      await refreshSession();
      toast.success("Caja abierta");
      navigate("/pos");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error abriendo caja");
    }
  };

  const doMovement = async (e) => {
    e.preventDefault();
    if (!session) return;
    const amt = parseFloat(movAmount || "0");
    if (amt <= 0) return;
    await addMovement({ kind: movKind, amount: amt, reason: movReason }, session.id, user.id);
    setMovAmount("");
    setMovReason("");
    reload();
    toast.success("Movimiento registrado");
  };

  const doClose = async (e) => {
    e.preventDefault();
    if (!session) return;
    try {
      const r = await closeCash({ closing_counted: parseFloat(counted || "0"), notes }, session.id);
      toast.success(`Caja cerrada. Diferencia: ${r.closing_diff.toFixed(2)}€`);
      setClosing(false);
      setCounted("");
      setNotes("");
      await refreshSession();
      setSummary(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cerrando caja");
    }
  };

  const onToggleSage = async () => {
    const r = await sageToggle();
    toast.message(`Sage 200c ahora ${r.online ? "ONLINE" : "OFFLINE"}`);
    refreshSage();
  };

  const onResyncSage = async () => {
    const r = await sageResync();
    toast.success(`${r.synced} documento(s) sincronizados`);
    refreshSage();
  };

  if (!session) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Apertura de caja</div>
          <h1 className="font-display text-3xl font-semibold mt-1 text-slate-900">Iniciar turno</h1>
          <p className="text-sm text-slate-500 mt-1">
            Indica el fondo inicial de caja antes de operar. Sólo se permite una sesión abierta por cajero.
          </p>
          <form onSubmit={doOpen} className="mt-6 space-y-3">
            <label className="text-xs uppercase tracking-wider text-slate-500">Fondo de caja inicial (€)</label>
            <input
              data-testid={TID.openCashInput}
              value={opening}
              onChange={(e) => setOpening(e.target.value.replace(",", "."))}
              inputMode="decimal"
              className="w-full h-14 px-4 rounded-md border border-slate-200 font-mono text-2xl text-right"
            />
            <button
              data-testid={TID.openCashSubmit}
              type="submit"
              className="w-full h-14 rounded-md bg-slate-900 text-white font-medium hover:bg-slate-800 flex items-center justify-center gap-2"
            >
              <Unlock size={18} /> Abrir caja
            </button>
          </form>
        </div>
      </div>
    );
  }

  const s = summary || {};

  return (
    <div className="h-full overflow-y-auto thin-scroll p-6 bg-slate-50">
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Sesión de caja activa</div>
            <h1 className="font-display text-3xl font-semibold text-slate-900">
              Caja {session.till_id}
            </h1>
            <div className="text-sm text-slate-500">
              Abierta por {session.user_name} · {new Date(session.opened_at).toLocaleString("es-ES")}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              data-testid={TID.cashXReport}
              onClick={reload}
              className="h-11 px-4 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-sm flex items-center gap-2"
            >
              <RotateCw size={14} /> Refrescar informe X
            </button>
            <button
              onClick={() => setBlind((b) => !b)}
              className="h-11 px-4 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-sm flex items-center gap-2"
            >
              {blind ? <Eye size={14} /> : <EyeOff size={14} />} Arqueo {blind ? "ciego" : "abierto"}
            </button>
            <button
              data-testid={TID.cashCloseBtn}
              onClick={() => setClosing(true)}
              className="h-11 px-4 rounded-md bg-red-600 text-white hover:bg-red-700 text-sm font-medium flex items-center gap-2"
            >
              <Lock size={14} /> Cerrar caja (Z)
            </button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Ventas netas" value={`${(s.net_sales ?? 0).toFixed(2)}€`} mute={blind} />
          <Kpi label="Nº tickets" value={s.n_tickets ?? 0} mono />
          <Kpi label="Ticket medio" value={`${(s.average_ticket ?? 0).toFixed(2)}€`} mute={blind} />
          <Kpi label="Devoluciones" value={`${(s.total_refunds ?? 0).toFixed(2)}€`} mute={blind} tone="danger" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Métodos pago */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Por método</div>
            <div className="mt-3 space-y-2">
              {Object.entries(s.methods || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className="capitalize text-slate-700">{k}</span>
                  <span className="font-mono font-semibold text-slate-900">
                    {blind && k !== "efectivo" ? "•••" : `${Number(v).toFixed(2)}€`}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-slate-200 text-sm space-y-1">
              <Row label="Fondo apertura" value={`${session.opening_amount.toFixed(2)}€`} />
              <Row label="Entradas" value={`${(s.movements_in ?? 0).toFixed(2)}€`} />
              <Row label="Salidas" value={`-${(s.movements_out ?? 0).toFixed(2)}€`} />
              <Row
                label="Efectivo esperado"
                value={blind ? "•••" : `${(s.expected_cash ?? 0).toFixed(2)}€`}
                bold
              />
            </div>
          </div>

          {/* Movimientos */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Movimientos de caja</div>
            <form onSubmit={doMovement} className="mt-3 space-y-2">
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid={TID.cashMoveIn}
                  onClick={() => setMovKind("entrada")}
                  className={`flex-1 h-10 rounded-md text-sm font-medium flex items-center justify-center gap-1 ${
                    movKind === "entrada" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700"
                  }`}
                >
                  <ArrowDownToLine size={14} /> Entrada
                </button>
                <button
                  type="button"
                  data-testid={TID.cashMoveOut}
                  onClick={() => setMovKind("salida")}
                  className={`flex-1 h-10 rounded-md text-sm font-medium flex items-center justify-center gap-1 ${
                    movKind === "salida" ? "bg-red-600 text-white" : "bg-slate-100 text-slate-700"
                  }`}
                >
                  <ArrowUpFromLine size={14} /> Salida
                </button>
              </div>
              <input
                value={movAmount}
                onChange={(e) => setMovAmount(e.target.value.replace(",", "."))}
                placeholder="Importe (€)"
                inputMode="decimal"
                className="w-full h-11 px-3 rounded-md border border-slate-200 font-mono text-right"
              />
              <input
                value={movReason}
                onChange={(e) => setMovReason(e.target.value)}
                placeholder="Motivo (sangría, pago proveedor…)"
                className="w-full h-11 px-3 rounded-md border border-slate-200 text-sm"
              />
              <button className="w-full h-11 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">
                Registrar movimiento
              </button>
            </form>
          </div>

          {/* Sage panel */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Sage 200c · Demo</div>
            <div className="mt-3 text-sm text-slate-700 leading-relaxed">
              Mock del conector ERP. En producción el TPV enviaría albaranes/pedidos por API
              y Sage actuaría como SIF, generando los registros Verifactu certificados.
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                onClick={onToggleSage}
                className="h-11 rounded-md border border-slate-200 hover:bg-slate-50 text-sm flex items-center justify-center gap-1"
              >
                <Power size={14} /> Toggle estado
              </button>
              <button
                onClick={onResyncSage}
                className="h-11 rounded-md border border-slate-200 hover:bg-slate-50 text-sm flex items-center justify-center gap-1"
              >
                <RotateCw size={14} /> Resync cola
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Close cash modal */}
      {closing && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={doClose} className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Cierre Z · Arqueo {blind ? "ciego" : "abierto"}</div>
            <h2 className="font-display text-2xl font-semibold mt-1 text-slate-900">Contar efectivo</h2>
            <p className="text-sm text-slate-500 mt-1">
              {blind
                ? "Cuenta el efectivo físico antes de ver el esperado del sistema. La diferencia se calcula al confirmar."
                : `Efectivo esperado por el sistema: ${(s.expected_cash ?? 0).toFixed(2)}€`}
            </p>
            <label className="block mt-4 text-xs uppercase tracking-wider text-slate-500">Efectivo contado</label>
            <input
              data-testid={TID.cashCountedInput}
              autoFocus
              value={counted}
              onChange={(e) => setCounted(e.target.value.replace(",", "."))}
              inputMode="decimal"
              className="w-full h-14 px-4 rounded-md border border-slate-200 font-mono text-2xl text-right mt-1"
            />
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas / incidencias del turno…"
              className="w-full h-20 mt-3 p-3 rounded-md border border-slate-200 text-sm"
            />
            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={() => setClosing(false)}
                className="flex-1 h-12 rounded-md border border-slate-200 hover:bg-slate-50 text-sm"
              >
                Cancelar
              </button>
              <button
                data-testid={TID.cashCloseConfirm}
                type="submit"
                className="flex-1 h-12 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700"
              >
                Cerrar caja y emitir Z
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, mute, mono = false, tone }) {
  return (
    <div className={`rounded-xl border p-4 ${tone === "danger" ? "bg-red-50 border-red-200" : "bg-white border-slate-200"}`}>
      <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">{label}</div>
      <div className={`mt-1 font-display text-2xl font-semibold ${mono ? "font-mono" : ""} ${tone === "danger" ? "text-red-700" : "text-slate-900"}`}>
        {mute && typeof value === "string" && value.includes("€") ? "•••" : value}
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-600">{label}</span>
      <span className={`font-mono ${bold ? "font-semibold text-slate-900" : "text-slate-800"}`}>{value}</span>
    </div>
  );
}
